<template>
    <v-container
        class="pa-6 justify-center"
        style="height: 100%; width: 600px"
        v-if="selectedRecord && isReview"
    >
        <div v-if="loading" class="text-xs-center" style="padding: 50px">
            <v-progress-circular indeterminate color="secondary" />
        </div>

        <div v-else>
            <v-card>
                <RecordDetails recordType="Review" />
                <v-card-text>
                    <h3 class="headline mb-0 text--typography1">
                        Review Comments
                    </h3>
                    <p class="text-body-large text--typography1">
                        Create new discussion threads here. Discussion threads
                        may only be started by the main reviewer, but anyone can
                        reply.
                    </p>

                    <v-container
                        :v-if="comments"
                        fluid
                        v-for="(thread, index) in comments"
                        :key="index"
                        style="padding: 0px"
                    >
                        <div
                            v-for="(comment, jindex) in thread"
                            :key="jindex"
                            v-bind:class="[
                                jindex !== 0 ? 'second-comment' : '',
                            ]"
                        >
                            <v-row>
                                <v-col cols="auto" class="pa-1">
                                    <UserMenu
                                        :onlyPhoto="true"
                                        :email="comment.created_by.email"
                                    />
                                </v-col>
                                <v-col class="font-weight-regular comment">{{
                                    comment.content
                                }}</v-col>
                            </v-row>
                            <v-row>
                                <v-col class="font-weight-thin date">
                                    {{ convertDate(comment.created_at) }}
                                </v-col>
                            </v-row>
                        </div>
                        <v-textarea
                            disabled
                            rows="1"
                            auto-grow
                            style="
                                padding-left: 50px;
                                padding-top: 5px;
                                font-size: 14px;
                            "
                            append-inner-icon="mdi-send"
                            @click:append-inner="addReplyComment(index)"
                            v-model="commentReplies[index]"
                            placeholder="Reply to Comment"
                        >
                        </v-textarea>
                        <br />
                    </v-container>
                    <v-textarea
                        disabled
                        v-if="recordOwner"
                        rows="1"
                        auto-grow
                        append-inner-icon="mdi-send"
                        @click:append-inner="createNewThread()"
                        placeholder="Make a New Thread"
                        v-model="newThread"
                    ></v-textarea>
                </v-card-text>
            </v-card>
        </div>
    </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import UserMenu from '../../UserMenu.vue'
import RecordDetails from './RecordDetails.vue'

const store = useStore()
const route = useRoute()

const comments = ref([])
const newThread = ref('')
const commentReplies = ref([])
const loading = ref(true)

const selectedRecord = computed(() => store.state.selectedRecord)
const user = computed(() => store.state.user)

const isReview = computed(() => store.dispatch('isReview'))

const recordOwner = computed(() => {
    if (user.value && user.value.mail === selectedRecord.value.created_by.email)
        return true
    return false
})

onMounted(() => initialise())

watch(route, () => initialise())

async function initialise() {
    comments.value = []
    await store.dispatch('getRecord', route.params.recordId)
    await store.dispatch('getFlowId', selectedRecord.value.type.flow_id)
    const c = await store.dispatch(
        'getRecordComments',
        selectedRecord.value.reference_id
    )
    sortComments(c)
    loading.value = false
}

function sortComments(c) {
    const postComments = c.filter((com) => com.level !== 0)
    if (postComments) makeIntoThreads(postComments)
}

function lastCommentIdInThread(threadIndex) {
    const thread = comments.value[threadIndex]
    return thread[thread.length - 1].id
}

function makeIntoThreads(c) {
    let threads = []
    let thread = []
    for (let i = 0; i < c.length; i++) {
        if (c[i].level === 1) {
            thread.push(c[i])
            let replyFound = true
            let currentComment = c[i]
            while (replyFound) {
                replyFound = false
                for (let j = 0; j < c.length; j++) {
                    if (c[j].parent === currentComment.id && c[j].level === 2) {
                        thread.push(c[j])
                        currentComment = c[j]
                        replyFound = true
                    }
                }
            }
            threads.push(thread)
            thread = []
        }
    }
    comments.value = threads
    createEmptyArrayForReplies(threads.length)
}

function createEmptyArrayForReplies(amount) {
    let array = []
    for (let i = 0; i < amount; i++) array.push([])
    commentReplies.value = array
}

async function createNewThread() {
    const url = import.meta.env.VUE_APP_API_URL + route.path
    let response = await store.dispatch('createComment', {
        parent: selectedRecord.value.reference_id,
        content: newThread.value,
        refUrl: url,
        referenceId: selectedRecord.value.reference_id,
        level: 1,
    })
    let array = [response]
    comments.value.push(array)
    newThread.value = ''
}

async function addReplyComment(i) {
    let c = comments.value
    let replies = commentReplies.value
    if (typeof replies[i] === 'string' && replies[i] !== '') {
        const url = import.meta.env.VUE_APP_API_URL + route.path
        const response = await store.dispatch('createComment', {
            parent: lastCommentIdInThread(i),
            content: replies[i],
            refUrl: url,
            referenceId: selectedRecord.value.reference_id,
            level: 2,
        })
        let thread = c[i]
        thread.push(response)
        c[i] = thread
        replies[i] = ''
        comments.value = c
        commentReplies.value = replies
    }
}

function convertDate(date) {
    let dates = Date.parse(date)
    let string = new Date(dates).toDateString()
    string = string.substring(0, string.length - 4)
    return string.substring(string.indexOf(' ') + 1)
}
</script>

<style scoped>
.comment {
    margin: 4px;
    background-color: #e0e0e0;
    border-radius: 10px;
    padding: 5px 5px 5px 10px;
    white-space: pre-line;
}

.date {
    font-size: 12px;
    display: flex;
    justify-content: flex-end;
    padding-right: 5px;
}

.second-comment {
    padding-left: 40px !important;
}

#submit-btn {
    color: white;
    margin: 5px;
    align-content: center;
}
</style>
